import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Play, Pause, Bookmark, Trash2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/atoms/Icon';
import { IconButton } from '@/components/atoms/IconButton';
import styles from './VocabCard.module.scss';
import type { VocabCardProps } from './VocabCard.types';

/**
 * VocabCard — fila compacta de vocabulario (play a la izquierda).
 * Con `detailHref`, la fila enlaza a la ficha completa.
 */
export function VocabCard({
  item,
  isSaved = false,
  onToggleSave,
  detailHref,
  onRowClick,
  showAudioButton = true,
  showSaveButton = true,
  saveActionVariant = 'bookmark',
  showTranslation = true,
  className,
  ...props
}: VocabCardProps) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlayAudio = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!item.wordAudioUrl) return;

    if (!audioRef.current) {
      audioRef.current = new Audio(item.wordAudioUrl);
      audioRef.current.onended = () => setIsPlaying(false);
    }

    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    } else {
      void audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleToggleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleSave?.(item);
  };

  const headerClass = styles.vocabCard__header;
  const translationLine = item.translationEs?.trim();

  const rowInner = (
    <>
      {showAudioButton ? (
        <button
          type="button"
          className={styles.vocabCard__playBtn}
          onClick={handlePlayAudio}
          disabled={!item.wordAudioUrl}
          aria-label={
            isPlaying
              ? t('vocabularyUi.pauseWord', { word: item.word })
              : t('vocabularyUi.playWordPronunciation', { word: item.word })
          }
        >
          <Icon icon={isPlaying ? Pause : Play} size="sm" aria-hidden />
        </button>
      ) : null}

      <div className={styles.vocabCard__itemBody}>
        <div className={styles.vocabCard__wordGroup}>
          <span className={styles.vocabCard__word}>{item.word}</span>
          {item.partOfSpeech && item.partOfSpeech !== '—' ? (
            <span className={styles.vocabCard__pos}>{item.partOfSpeech}</span>
          ) : null}
        </div>
        {showTranslation && translationLine ? (
          <span className={styles.vocabCard__translation}>{translationLine}</span>
        ) : null}
      </div>

      {showSaveButton && onToggleSave ? (
        <div className={styles.vocabCard__actions}>
          {saveActionVariant === 'trash' ? (
            <IconButton
              icon={Trash2}
              variant="bare"
              buttonSize="sm"
              size="sm"
              onClick={handleToggleSave}
              aria-label={t('activities.removeVocabAria', { word: item.word })}
            />
          ) : (
            <IconButton
              icon={Bookmark}
              variant="bare"
              buttonSize="sm"
              size="sm"
              onClick={handleToggleSave}
              aria-label={
                isSaved
                  ? t('activities.removeVocabAria', { word: item.word })
                  : t('activities.saveVocabAria', { word: item.word })
              }
            />
          )}
        </div>
      ) : null}
    </>
  );

  return (
    <div className={cn(styles.vocabCard, className)} {...props}>
      {onRowClick ? (
        <button type="button" className={headerClass} data-no-press-scale="" onClick={() => onRowClick(item)}>
          {rowInner}
        </button>
      ) : detailHref ? (
        <Link to={detailHref} className={headerClass}>
          {rowInner}
        </Link>
      ) : (
        <div className={headerClass} role="presentation">
          {rowInner}
        </div>
      )}
    </div>
  );
}

VocabCard.displayName = 'VocabCard';
export default VocabCard;
